import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  useTheme, 
  IconButton, 
  Paper, 
  Toolbar, 
  TextField, 
  Divider, 
  Stack 
} from '@mui/material';
import {
  ArrowBackIos,
  ArrowForwardIos,
  ZoomIn,
  ZoomOut,
  GetApp,
  FitScreen,
  FirstPage,
  LastPage,
  Refresh
} from '@mui/icons-material';

// Global flag to track PDF.js loading state
let pdfjsLoadingPromise: Promise<void> | null = null;

// Load PDF.js from CDN and configure worker
const loadPDFJS = (): Promise<void> => {
  if (pdfjsLoadingPromise) {
    return pdfjsLoadingPromise;
  }

  if (typeof window !== 'undefined' && (window as any).pdfjsLib) {
    return Promise.resolve();
  }

  pdfjsLoadingPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window is not available'));
      return;
    }

    // Load PDF.js script
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.min.js';
    script.onload = () => {
      // Configure worker after script loads
      if ((window as any).pdfjsLib) {
        (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js';
        console.log('PDF.js loaded and configured');
        resolve();
      } else {
        reject(new Error('PDF.js failed to load'));
      }
    };
    script.onerror = () => {
      reject(new Error('Failed to load PDF.js script'));
    };
    document.head.appendChild(script);
  });

  return pdfjsLoadingPromise;
};

interface CanvasPdfViewerProps {
  url: string;
  fileName?: string;
  initialScale?: number;
}

export const CanvasPdfViewer: React.FC<CanvasPdfViewerProps> = ({ 
  url, 
  fileName, 
  initialScale = 1.0 
}) => {
  const theme = useTheme();
  
  // State
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(initialScale);
  const [rotation, setRotation] = useState(0);
  const [_, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen] = useState(false);
  const [pageInput, setPageInput] = useState('1');
  const [fitToWidth, setFitToWidth] = useState(false);

  // Refs
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const renderTaskRef = useRef<any>(null);

  // PDF rendering is now handled by useEffect

  // Load PDF from URL
  const loadPdfFromUrl = useCallback(async (pdfUrl: string) => {
    if (!pdfUrl || !pdfUrl.trim()) {
      setError('Invalid PDF URL provided');
      setLoading(false);
      return;
    }

    // Reset state
    setPdfDoc(null);
    setCurrentPage(1);
    setNumPages(null);
    setError(null);
    setLoading(true);
    setScale(initialScale);
    setRotation(0);
    setPageInput('1');

    try {
      // Wait for PDF.js to load properly
      console.log('Waiting for PDF.js to load...');
      await loadPDFJS();
      console.log('PDF.js is ready');

      // Ensure PDF.js is available
      if (!(window as any).pdfjsLib) {
        throw new Error('PDF.js library is not available');
      }

      // Load PDF using ArrayBuffer approach for better CORS handling
      console.log('Fetching PDF from:', pdfUrl);
      const response = await fetch(pdfUrl, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('Converting to ArrayBuffer...');
      const arrayBuffer = await response.arrayBuffer();
      
      console.log('Creating PDF document...');
      const loadingTask = (window as any).pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      console.log('PDF loaded successfully, pages:', pdf.numPages);
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setCurrentPage(1);
      setLoading(false);

      // Note: renderPage will be called by useEffect when pdfDoc changes
    } catch (err: any) {
      console.error('Error loading PDF:', err);
      setError(err?.message || 'Unable to load PDF preview');
      setLoading(false);
    }
  }, [initialScale]); // Removed renderPage dependency

  // Helper functions
  const goToPage = useCallback((pageNumber: number) => {
    const newPage = Math.max(1, Math.min(numPages || 1, pageNumber));
    setCurrentPage(newPage);
    setPageInput(newPage.toString());
    // renderPage will be called by useEffect when currentPage changes
  }, [numPages]);

  const handlePageInputChange = (value: string) => {
    setPageInput(value);
    const pageNum = parseInt(value);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= (numPages || 1)) {
      goToPage(pageNum);
    }
  };

  const changeScale = useCallback((newScale: number) => {
    const clampedScale = Math.max(0.25, Math.min(3, newScale));
    setScale(clampedScale);
    setFitToWidth(false);
    // renderPage will be called by useEffect when scale changes
  }, []);

  // const changeRotation = useCallback((deltaRotation: number) => {
  //   const newRotation = (rotation + deltaRotation) % 360;
  //   setRotation(newRotation);
  //   // renderPage will be called by useEffect when rotation changes
  // }, [rotation]);

  const fitToContainer = useCallback(() => {
    if (containerRef.current && canvasRef.current) {
      const containerWidth = containerRef.current.clientWidth - 32;
      const currentCanvasWidth = parseInt(canvasRef.current.style.width) || 600;
      const newScale = containerWidth / currentCanvasWidth;
      setScale(Math.max(0.5, Math.min(3, newScale)));
      setFitToWidth(true);
      // renderPage will be called by useEffect when scale changes
    }
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1.0);
    setFitToWidth(false);
    // renderPage will be called by useEffect when scale changes
  }, []);

  // const toggleFullscreen = () => {
  //   setIsFullscreen(!isFullscreen);
  // };

  const download = () => {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || url.split('/').pop() || 'document.pdf';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // const printPdf = () => {
  //   const w = window.open(url, '_blank');
  //   if (!w) return;
  //   w.focus();
  //   setTimeout(() => {
  //     try { w.print(); } catch (e) { console.warn('Print failed', e); }
  //   }, 500);
  // };

  // Load PDF when URL changes
  useEffect(() => {
    if (url) {
      loadPdfFromUrl(url);
    }
  }, [url, loadPdfFromUrl]);

  // Render page when PDF document is loaded or any render parameter changes
  useEffect(() => {
    if (!pdfDoc || !currentPage || !canvasRef.current) {
      console.log('Skipping render - missing dependencies:', { 
        hasPdfDoc: !!pdfDoc, 
        hasCurrentPage: !!currentPage, 
        hasCanvasRef: !!canvasRef.current 
      });
      return;
    }

    // Cancel any previous render task
    if (renderTaskRef.current) {
      console.log('Cancelling previous render task');
      try {
        renderTaskRef.current.cancel();
      } catch (e) {
        console.log('Previous render task already completed or cancelled');
      }
      renderTaskRef.current = null;
    }
    
    console.log(`Starting to render page ${currentPage} at scale ${scale}, rotation ${rotation}`);
    
    const renderCurrentPage = async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        const canvas = canvasRef.current;
        const ctx = canvas!.getContext('2d');
        
        if (!ctx) {
          console.error('Canvas context not available');
          return;
        }
        
        // Calculate viewport with scale and rotation
        const viewport = page.getViewport({ scale, rotation });
        
        // Set canvas size to match PDF page aspect ratio
        const ratio = window.devicePixelRatio || 1;
        canvas!.width = Math.floor(viewport.width * ratio);
        canvas!.height = Math.floor(viewport.height * ratio);
        canvas!.style.width = viewport.width + 'px';
        canvas!.style.height = viewport.height + 'px';
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas!.width, canvas!.height);
        
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
          transform: [ratio, 0, 0, ratio, 0, 0],
        };
        
        // Start render and store the render task
        renderTaskRef.current = page.render(renderContext);
        await renderTaskRef.current.promise;
        renderTaskRef.current = null;
        console.log(`Successfully rendered page ${currentPage} at scale ${scale}`);
      } catch (err: any) {
        if (err.name === 'RenderingCancelledException') {
          console.log('PDF rendering was cancelled');
        } else {
          console.error('Error rendering page:', err);
          setError('Failed to render PDF page');
        }
      }
    };
    
    renderCurrentPage();

    // Cleanup function
    return () => {
      if (renderTaskRef.current) {
        console.log('Cleaning up render task');
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          console.log('Render task already completed or cancelled');
        }
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, scale, rotation]);

  // Update page input when current page changes
  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        console.log('Component unmounting - cancelling render task');
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          console.log('Render task already completed or cancelled on unmount');
        }
        renderTaskRef.current = null;
      }
    };
  }, []);

  return (
    <Paper 
      elevation={isFullscreen ? 8 : 2} 
      sx={{
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        width: isFullscreen ? '100vw' : '100%',
        height: isFullscreen ? '100vh' : 'auto',
        zIndex: isFullscreen ? 1300 : 'auto',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isFullscreen ? 'background.default' : 'background.paper',
      }}
    >
      {/* Header with file info */}
      {/* <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <PictureAsPdf sx={{ color: 'error.main', fontSize: 28 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
              {fileName || 'PDF Document'}
            </Typography>
            {numPages && (
              <Typography variant="caption" color="text.secondary">
                {numPages} page{numPages > 1 ? 's' : ''} • {Math.round(scale * 100)}% zoom
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip 
              label={`Page ${currentPage}${numPages ? ` of ${numPages}` : ''}`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
            <IconButton size="small" onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Stack>
        </Box>
        
        {loading && (
          <Box sx={{ width: '100%', mt: 1 }}>
            <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Loading PDF...
            </Typography>
          </Box>
        )}
      </Box> */}

      {/* Advanced Toolbar */}
      <Toolbar variant="dense" sx={{ gap: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' , justifyContent:'center', display:'flex'}}>
        {/* Navigation Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton 
            size="small" 
            disabled={!numPages || currentPage <= 1} 
            onClick={() => goToPage(1)}
            title="First page"
          >
            <FirstPage fontSize="small" />
          </IconButton>
          
          <IconButton 
            size="small" 
            disabled={!numPages || currentPage <= 1} 
            onClick={() => goToPage(currentPage - 1)}
            title="Previous page"
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>

          <TextField
            value={pageInput}
            onChange={(e) => handlePageInputChange(e.target.value)}
            onBlur={() => setPageInput(currentPage.toString())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const pageNum = parseInt(pageInput);
                if (!isNaN(pageNum)) goToPage(pageNum);
              }
            }}
            size="small"
            sx={{ 
              width: 60,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                height: 32,
              }
            }}
            disabled={!numPages}
          />

          <Typography variant="body2" sx={{ mx: 0.5 }}>
            of {numPages || '?'}
          </Typography>

          <IconButton 
            size="small" 
            disabled={!numPages || currentPage >= numPages} 
            onClick={() => goToPage(currentPage + 1)}
            title="Next page"
          >
            <ArrowForwardIos fontSize="small" />
          </IconButton>

          <IconButton 
            size="small" 
            disabled={!numPages || currentPage >= numPages} 
            onClick={() => goToPage(numPages || 1)}
            title="Last page"
          >
            <LastPage fontSize="small" />
          </IconButton>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Zoom Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton 
            size="small" 
            onClick={() => changeScale(scale - 0.25)}
            disabled={scale <= 0.25}
            title="Zoom out"
          >
            <ZoomOut fontSize="small" />
          </IconButton>

          <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Typography>

          <IconButton 
            size="small" 
            onClick={() => changeScale(scale + 0.25)}
            disabled={scale >= 3}
            title="Zoom in"
          >
            <ZoomIn fontSize="small" />
          </IconButton>

          <IconButton 
            size="small" 
            onClick={fitToContainer}
            title="Fit to width"
            color={fitToWidth ? 'primary' : 'default'}
          >
            <FitScreen fontSize="small" />
          </IconButton>

          <IconButton 
            size="small" 
            onClick={resetZoom}
            title="Reset zoom"
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Rotation & Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* <IconButton 
            size="small" 
            onClick={() => changeRotation(-90)}
            title="Rotate left"
          >
            <RotateLeft fontSize="small" />
          </IconButton>

          <IconButton 
            size="small" 
            onClick={() => changeRotation(90)}
            title="Rotate right"
          >
            <RotateRight fontSize="small" />
          </IconButton> */}

          <IconButton 
            size="small" 
            onClick={download}
            title="Download"
          >
            <GetApp fontSize="small" />
          </IconButton>

          {/* <IconButton 
            size="small" 
            onClick={printPdf}
            title="Print"
          >
            <Print fontSize="small" />
          </IconButton> */}
        </Box>
      </Toolbar>

      {/* PDF Canvas Area */}
      <Box 
        ref={containerRef} 
        sx={{ 
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
          overflow: 'auto',
          minHeight: isFullscreen ? 'calc(100vh - 120px)' : 500,
        }}
      >
        {error ? (
          <Paper sx={{ p: 3, textAlign: 'center', maxWidth: 500 }}>
            <Typography color="error" variant="h6" gutterBottom>
              Failed to load PDF
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {error}
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
              <IconButton
                color="primary"
                onClick={() => loadPdfFromUrl(url)}
                title="Retry loading"
                sx={{ border: 1, borderColor: 'primary.main' }}
              >
                <Refresh />
              </IconButton>
              <IconButton
                color="primary"
                onClick={download}
                title="Download PDF"
                sx={{ border: 1, borderColor: 'primary.main' }}
              >
                <GetApp />
              </IconButton>
            </Stack>
            
            {url && url.trim() && (
              <>
                <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                  Alternative preview:
                </Typography>
                <Box
                  component="iframe"
                  src={url}
                  sx={{ 
                    width: '100%', 
                    height: 300, 
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                  title={fileName || 'PDF Preview'}
                  onError={() => console.error('Iframe fallback also failed')}
                />
              </>
            )}
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Paper 
              elevation={4}
              sx={{ 
                display: 'inline-block',
                overflow: 'hidden',
                transition: 'box-shadow 0.3s ease',
                '&:hover': {
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
            </Paper>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default CanvasPdfViewer;